import React from 'react';
import { useMapProvider } from './MapProvider';
import { StaticMapImageProps, StaticMapConfig } from './StaticMapInterface';
import { useStaticMapCache } from './StaticMapCache';
import MapboxStaticImage from './MapboxStaticImage';
import GoogleStaticImage from './GoogleStaticImage';

/**
 * StaticMapImage Component
 * 
 * Renders static map images using either Google Maps or Mapbox Static APIs
 * based on the admin-selected provider. Provides better performance for
 * non-interactive map displays like public profiles.
 */
const StaticMapImage: React.FC<StaticMapImageProps> = ({
  entries,
  center,
  zoom = 10,
  width,
  height,
  showMarkers = true,
  className,
  style,
  onError,
  onLoad,
  alt = 'Static map showing travel locations',
  mapType = 'roadmap',
  ...props
}) => {
  const { provider, isLoaded, error } = useMapProvider();
    const { getCachedMapUrl, setCachedMapUrl } = useStaticMapCache();

  // Get API keys from environment variables
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapboxAccessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  // Determine base URL for custom pins
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? window.location.origin
    : 'http://localhost:3000';

  // Handle loading state
  if (!isLoaded) {
    return (
      <div 
        className={className}
        style={{
          ...style,
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '1px solid #ddd',
          color: '#666'
        }}
      >
        Loading map...
      </div>
    );
  }

  // Handle map provider error
  if (error) {
    const errorMessage = `Map provider error: ${error}`;
    console.error(errorMessage);
    
    if (onError) {
      onError(new Error(errorMessage));
    }
    
    return (
      <div 
        className={className}
        style={{
          ...style,
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffebee',
          border: '1px solid #f44336',
          color: '#d32f2f'
        }}
      >
        Failed to load map
      </div>
    );
  }

  // Prepare configuration
  const apiKey = provider === 'mapbox' ? mapboxAccessToken : googleMapsApiKey;
  const config: StaticMapConfig = {
    provider,
    apiKey: apiKey || '',
    baseUrl,
  };

  // Validate API key
  if (!config.apiKey) {
    const errorMessage = `Missing API key for ${provider} provider`;
    console.error(errorMessage);
    
    if (onError) {
      onError(new Error(errorMessage));
    }
    
    return (
      <div 
        className={className}
        style={{
          ...style,
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          color: '#856404'
        }}
      >
        Map configuration error
      </div>
    );
  }

  // Common props for provider components
  const commonProps = {
    entries,
    center,
    zoom,
    width,
    height,
    showMarkers,
    className,
    style,
    onError,
    onLoad,
    alt,
    mapType,
    apiKey: config.apiKey,
    baseUrl: config.baseUrl,
    ...props
  };

  // Render appropriate provider component
  try {
    if (provider === 'mapbox') {
      return <MapboxStaticImage {...commonProps} />;
    } else {
      return <GoogleStaticImage {...commonProps} />;
    }
  } catch (renderError) {
    const errorMessage = `Failed to render ${provider} static map: ${renderError}`;
    console.error(errorMessage);
    
    if (onError) {
      onError(renderError instanceof Error ? renderError : new Error(errorMessage));
    }
    
    return (
      <div 
        className={className}
        style={{
          ...style,
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffebee',
          border: '1px solid #f44336',
          color: '#d32f2f'
        }}
      >
        Render error
      </div>
    );
  }
};

export default StaticMapImage;